import type { DepthPoint } from '@/domain/depthSeries';
import { extractDayInTimezone } from '@/utils/date';
import { wallTimeInZone } from '@/utils/date/zonedWallTime';

export const MS_PER_24_HOURS = 24 * 60 * 60 * 1000;

/** Last reading minus the first, in metres. Positive means the bioscope sits deeper. */
export function depthChange(points: DepthPoint[]): number | null {
    const values = points.flatMap((point) => (point.value == null ? [] : [point.value]));
    if (values.length < 2) return null;
    return values[values.length - 1] - values[0];
}

/** Yesterday and today on the site's calendar. The last 24 hours can start yesterday, so both days are fetched. */
export function siteDepthWindow(now: Date, timeZone: string): { from: string; to: string } {
    const to = extractDayInTimezone(now.toISOString(), timeZone);
    const from = new Date(`${to}T00:00:00Z`);
    from.setUTCDate(from.getUTCDate() - 1);
    return { from: from.toISOString().slice(0, 10), to };
}

/**
 * Points whose site wall-clock hour falls in the 24 hours up to `now`.
 *
 * `time` has no offset. Reading it in the browser's zone makes a recent Oslo hour look
 * like a future time to someone in Los Angeles, and the card drops it.
 */
export function pointsInLast24Hours(
    points: DepthPoint[],
    now: Date,
    timeZone: string
): DepthPoint[] {
    const nowMs = now.getTime();
    const cutoff = nowMs - MS_PER_24_HOURS;
    return points.filter((point) => {
        const time = wallTimeInZone(point.time, timeZone).getTime();
        return !Number.isNaN(time) && time >= cutoff && time <= nowMs;
    });
}

/** The same points, with each hour as an instant so a sparkline can place it. */
export function timedDepthPoints(
    points: DepthPoint[],
    timeZone: string
): Array<{ at: number; value: number | null }> {
    return points.flatMap((point) => {
        const at = wallTimeInZone(point.time, timeZone).getTime();
        return Number.isNaN(at) ? [] : [{ at, value: point.value }];
    });
}
